import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    useInitiateElemisApplicationMutation,
    readElemisRefusal,
    type ElemisRefusal,
} from '@features/agreements';
import type { PaymentSelection } from '../types/schedule.types';
import { NO_LINK_DELIVERY, type LinkDeliveryChoice } from '../types/linkDelivery.types';
import type { CheckoutOutcome } from './useCheckoutFrameEvents';
import { QuoteConflictError, toQuoteSaveError } from '../services/quoteSaveErrors';
import type { InitiationTicket } from './useInitiationPrecondition';

export type QuoteJourney = 'dealer_collects' | 'motorplan_collects';

/** Accessibility spec, status text ladder (points 1-2). Both timers share one
 *  cleanup and are cleared together on settle. */
export const SLOW_CALL_MS = 8_000;
export const VERY_SLOW_CALL_MS = 60_000;

export interface ElemisCheckoutState {
    journey: QuoteJourney;
    canInitiate: boolean;
    /** Non-null exactly when the terminal control in the MotorPlan-collects
     *  branch is disabled FOR A REASON — rendered as VISIBLE text next to the
     *  button (accessibility spec point 6: a natively `disabled` button is
     *  out of the tab order, so `aria-describedby` alone is never enough). */
    disabledReason: string | null;
    isInitiating: boolean;
    /** Pending for more than SLOW_CALL_MS. */
    isSlow: boolean;
    /** Pending for more than VERY_SLOW_CALL_MS — the "safe to reload" rung. */
    isVerySlow: boolean;
    /** A CAPABILITY URL. `useState` ONLY — never Redux, never storage (F-D4). */
    checkoutUrl: string | null;
    /** The elemis application id the frame was opened for; a frame event must
     *  name it to be accepted (D17). */
    applicationId: string | null;
    agreementId: string | null;
    agreementRef: string | null;
    refusal: ElemisRefusal | null;
    /** True while the hosted checkout modal is showing. Opens on the first
     *  successful initiation; closing keeps `checkoutUrl` so it can reopen. */
    isCheckoutOpen: boolean;
    /** The frame's terminal report, a UI trigger only. Cleared once a
     *  replacement checkout exists, never merely because a retry was sent. */
    outcome: CheckoutOutcome | null;
    openCheckout: () => void;
    closeCheckout: () => void;
    receiveOutcome: (outcome: CheckoutOutcome) => void;
    /** True when the last initiate response was a replay of a live session while the
     *  request had asked for a link: the server sent no new link. */
    linkSuppressedByReplay: boolean;
    initiate: (delivery?: LinkDeliveryChoice) => void;
    /** A brand-new elemis application, superseding the dead one (`retry: true`). */
    retry: () => void;
}

/**
 * The SINGLE owner of the MotorPlan-collects journey's state (design F-D1,
 * V1). Instantiated exactly ONCE, in QuoteWizard, and its return value is
 * passed to both QuoteProgressNav copies and to QuoteSummaryStep.
 *
 * `canCompleteQuote` is threaded in as a plain boolean (QuoteWizard already
 * computes `quote.canCompleteQuote()` once, for `progressNavProps.canComplete`
 * — the same value, not re-derived) rather than this hook calling `useQuote()`
 * itself. That keeps the hook a pure function of its inputs, so
 * `useElemisCheckout.test.tsx` can exercise the ref guard, the slow timers and
 * the branch memo with `renderHook` and no `QuoteProvider` in the tree.
 */
export function useElemisCheckout(
    quoteId: string | undefined,
    paymentSelection: PaymentSelection | undefined,
    canCompleteQuote: boolean,
    hasInitiatePermission: boolean,
    defaultDelivery: LinkDeliveryChoice = NO_LINK_DELIVERY,
    beginInitiation?: () => InitiationTicket,
): ElemisCheckoutState {
    const mutation = useInitiateElemisApplicationMutation(quoteId ?? '');

    const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
    const [applicationId, setApplicationId] = useState<string | null>(null);
    const [agreementId, setAgreementId] = useState<string | null>(null);
    const [agreementRef, setAgreementRef] = useState<string | null>(null);
    const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
    const [outcome, setOutcome] = useState<CheckoutOutcome | null>(null);
    const [refusal, setRefusal] = useState<ElemisRefusal | null>(null);
    const [linkSuppressedByReplay, setLinkSuppressedByReplay] = useState(false);
    const lastDeliveryRef = useRef<LinkDeliveryChoice | null>(null);
    // F-D7: the server read the quote's payment selection and authoritatively
    // said dealer-collects. Recorded, never auto-executed — the user still presses.
    const [serverSaysDealerCollects, setServerSaysDealerCollects] = useState(false);
    const [isSlow, setIsSlow] = useState(false);
    const [isVerySlow, setIsVerySlow] = useState(false);

    // A synchronous guard, never `mutation.isPending` — the nav renders twice and
    // both clicks read one render. Holds the attempt: a stale settle cannot unlock.
    const attemptRef = useRef(0);
    const inFlightAttemptRef = useRef<number | null>(null);

    // Journey is a property of the sale, not of the product (design D2). The memo
    // depends on primitives, not `paymentSelection` itself (R3).
    const methodKey = paymentSelection?.methodKey;
    const isPaidInFull = methodKey === 'paid_in_full';
    // Paid-in-full is a dealer-collects journey only when the full payment is NOT
    // routed to Elemis — an Elemis single charge is motorplan-collects.
    const fullPaymentCollection = paymentSelection?.collectionRouting?.fullPaymentCollection;
    const isDealerCollectedInFull = isPaidInFull && fullPaymentCollection !== 'elemis';
    const clientJourney = useMemo<QuoteJourney>(
        () => (isDealerCollectedInFull ? 'dealer_collects' : 'motorplan_collects'),
        [isDealerCollectedInFull],
    );
    // The server's answer outranks the client's until the selection it was
    // computed from changes (F-D7, and the effect below).
    const journey: QuoteJourney = serverSaysDealerCollects ? 'dealer_collects' : clientJourney;

    const openCheckout = useCallback(() => {
        if (checkoutUrl !== null) setIsCheckoutOpen(true);
    }, [checkoutUrl]);

    const closeCheckout = useCallback(() => setIsCheckoutOpen(false), []);

    const receiveOutcome = useCallback((next: CheckoutOutcome) => {
        setOutcome(next);
        setIsCheckoutOpen(false);
    }, []);

    // Every value below belongs to ONE quote. This component outlives a Start
    // Over, so a new or cleared quote must never inherit its capability URL.
    const checkoutQuoteIdRef = useRef(quoteId);
    useEffect(() => {
        if (checkoutQuoteIdRef.current === quoteId) return;
        checkoutQuoteIdRef.current = quoteId;
        setCheckoutUrl(null);
        setApplicationId(null);
        setAgreementId(null);
        setAgreementRef(null);
        setIsCheckoutOpen(false);
        setOutcome(null);
        setRefusal(null);
        setLinkSuppressedByReplay(false);
        lastDeliveryRef.current = null;
        setServerSaysDealerCollects(false);
        // The abandoned request has no timeout and may never settle, so the new
        // quote is unlocked here rather than waiting on it.
        inFlightAttemptRef.current = null;
        mutation.reset();
        // `mutation` whole, not `.reset`: React Query hands back a new object on
        // every status change, and the guard above makes those re-runs no-ops.
    }, [quoteId, mutation]);

    // A paid-in-full refusal reads the LIVE selection, so every term the journey reads
    // retires it and the override it pinned — the routing, not just the method.
    const journeyKey = `${methodKey ?? ''}|${fullPaymentCollection ?? ''}`;
    const seenJourneyKeyRef = useRef(journeyKey);
    const journeyKeyEpochRef = useRef(0);
    useEffect(() => {
        if (seenJourneyKeyRef.current === journeyKey) return;
        seenJourneyKeyRef.current = journeyKey;
        journeyKeyEpochRef.current += 1;
        setServerSaysDealerCollects(false);
        setRefusal((prev) => (prev?.code === 'paid_in_full_journey' ? null : prev));
    }, [journeyKey]);

    // The one network path for both initiate() and retry(); `retry: true`
    // supersedes the live attempt server-side and returns a new application.
    const start = useCallback(
        (variables: { retry?: true; delivery: LinkDeliveryChoice }) => {
            if (
                inFlightAttemptRef.current !== null
                || journey !== 'motorplan_collects'
                || !quoteId
                || !canCompleteQuote
                || !hasInitiatePermission
            ) {
                return;
            }
            // Superseded the moment the quote changes, so a late result can
            // never reach the state of whatever replaced it.
            const attempt = attemptRef.current + 1;
            attemptRef.current = attempt;
            inFlightAttemptRef.current = attempt;
            const requestJourneyKeyEpoch = journeyKeyEpochRef.current;
            setRefusal(null);
            setLinkSuppressedByReplay(false);
            const { retry: isRetry, delivery } = variables;
            lastDeliveryRef.current = delivery;
            const requestedLink = delivery.sendViaEmail || delivery.sendViaSms;
            const ticket = beginInitiation?.();
            const request = {
                ...(isRetry ? { retry: true } : {}),
                expectedVersion: ticket?.expectedVersion,
                sendViaEmail: delivery.sendViaEmail,
                sendViaSms: delivery.sendViaSms,
            };
            mutation.mutate(request, {
                onSuccess: (app) => {
                    ticket?.settle(null);
                    if (inFlightAttemptRef.current !== attempt) return;
                    setCheckoutUrl(app.checkoutUrl);
                    setApplicationId(app.applicationId ?? null);
                    setAgreementId(app.agreementId ?? null);
                    setAgreementRef(app.agreementRef);
                    setIsCheckoutOpen(true);
                    // Only a live replacement retires a terminal outcome; a
                    // refused retry keeps it, so the dead checkout stays dead.
                    setOutcome(null);
                    setLinkSuppressedByReplay(app.replayed === true && requestedLink);
                },
                onError: (err) => {
                    if (inFlightAttemptRef.current !== attempt) {
                        ticket?.settle(null);
                        return;
                    }
                    const saveError = toQuoteSaveError(err);
                    if (ticket && saveError instanceof QuoteConflictError) {
                        ticket.settle(saveError);
                        return;
                    }
                    ticket?.settle(null);
                    const r = readElemisRefusal(err);
                    // A paid-in-full refusal judges the selection it was requested
                    // under; any mid-flight change to it makes it stale — drop it whole.
                    if (r.code === 'paid_in_full_journey') {
                        if (journeyKeyEpochRef.current !== requestJourneyKeyEpoch) return;
                        setRefusal(r);
                        setServerSaysDealerCollects(true);
                        return;
                    }
                    setRefusal(r);
                },
                onSettled: () => {
                    if (inFlightAttemptRef.current === attempt) inFlightAttemptRef.current = null;
                },
            });
        },
        // `mutation.mutate`, NOT `mutation` — React Query v5 returns a NEW
        // result object on every status change, so depending on the whole
        // `mutation` object would recreate this callback on every
        // pending/success/error transition, including for the two nav
        // copies that receive it as a prop. `mutate` itself is referentially
        // stable for the life of the hook, so listing only it (rather than
        // the object `eslint-plugin-react-hooks` wants) is deliberate, not
        // an oversight.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [journey, quoteId, canCompleteQuote, hasInitiatePermission, beginInitiation, mutation.mutate],
    );

    const initiate = useCallback(
        (delivery?: LinkDeliveryChoice) => {
            // A checkout that has reported a failure is dead: every control that
            // lands here must supersede it rather than reopen it.
            if (outcome !== null && outcome !== 'completed') {
                start({ retry: true, delivery: delivery ?? lastDeliveryRef.current ?? defaultDelivery });
                return;
            }
            // A URL already in hand is reopened locally: the server would only
            // replay the same live attempt, so the round-trip buys nothing.
            if (checkoutUrl !== null) {
                setIsCheckoutOpen(true);
                return;
            }
            start({ delivery: delivery ?? defaultDelivery });
        },
        [outcome, checkoutUrl, start, defaultDelivery],
    );

    const retry = useCallback(
        () => start({ retry: true, delivery: lastDeliveryRef.current ?? defaultDelivery }),
        [start, defaultDelivery],
    );

    // The progressive status timer. Dependency array is [mutation.isPending];
    // BOTH timers are cleared in the cleanup, so a fast call never leaves a
    // pending timeout that fires after settle and announces "still working"
    // (or "safe to reload") into a journey that has already finished.
    useEffect(() => {
        if (!mutation.isPending) {
            setIsSlow(false);
            setIsVerySlow(false);
            return;
        }
        const slowId = window.setTimeout(() => setIsSlow(true), SLOW_CALL_MS);
        const verySlowId = window.setTimeout(() => setIsVerySlow(true), VERY_SLOW_CALL_MS);
        return () => {
            window.clearTimeout(slowId);
            window.clearTimeout(verySlowId);
        };
    }, [mutation.isPending]);

    // The disabled-reason ladder — checked in this order, matching
    // `canInitiate`'s own precondition order below, so a failing precondition
    // always has exactly one matching, visible explanation.
    const disabledReason = useMemo<string | null>(() => {
        if (journey !== 'motorplan_collects') return null;
        if (!quoteId) return 'Save the quote before starting the payment journey.';
        if (!canCompleteQuote) {
            return 'Complete every previous step before starting the payment journey.';
        }
        if (!hasInitiatePermission) {
            return "You don't have permission to start the payment journey. Ask an administrator to grant it.";
        }
        // An in-flight request is not a blocked PRECONDITION (matches
        // QuoteWizard's existing blockReason precedent for `completingSale`)
        // — no reason is reported for it.
        return null;
    }, [journey, quoteId, canCompleteQuote, hasInitiatePermission]);

    const canInitiate =
        journey === 'motorplan_collects'
        && !!quoteId
        && canCompleteQuote
        && hasInitiatePermission
        && !mutation.isPending;

    return {
        journey,
        canInitiate,
        disabledReason,
        isInitiating: mutation.isPending,
        isSlow,
        isVerySlow,
        checkoutUrl,
        applicationId,
        agreementId,
        agreementRef,
        refusal,
        isCheckoutOpen,
        outcome,
        openCheckout,
        closeCheckout,
        receiveOutcome,
        linkSuppressedByReplay,
        initiate,
        retry,
    };
}
